import { useFetchWhiteningSummary } from '@/api/hooks/useFetchWhiteningSummary'
import { MembershipSheet } from '@/components/BillingPage/Sections/MembershipSheet'
import { StatusPill } from '@/components/Rows'
import { buildWhiteningView, tileInTransit, tilePill } from '@/features/whitening/state'
import { Sheet } from '@/ui/core/sheet'
import { HStack } from '@/ui/core/stack'
import { router } from 'expo-router'
import { useState } from 'react'
import { Tile } from './Tile'

/**
 * The pair under the hero: the two standing promises of the membership, in the same two places
 * every day. The first opens WHAT'S INCLUDED on the sheet — what the plan covers, the same one
 * the billing tile opens. The second opens the whitening kit's own page.
 *
 * The first says what it opens rather than what it belongs to. "Membership" named the thing a
 * member already knows they have; the sheet behind it is two lists, what the plan covers and
 * what it does not, which is the question actually being asked. It used to change tab to Care —
 * a different promise again.
 *
 * The kit's state comes from the server now, through the same summary its page is built from, so
 * the two cannot disagree about where somebody's kit is. It used to come from a flag on the
 * device, which meant a claim survived a relaunch but not a reinstall, and was never true for
 * anyone but the phone it was tapped on.
 */
export const TilePair = ({ planNew }: { planNew: number }) => {
	const { summary } = useFetchWhiteningSummary()
	const [membershipOpen, setMembershipOpen] = useState(false)

	const whitening = buildWhiteningView({ summary })
	// The refill's state once there is one to speak of, the kit's otherwise: a member whose kit
	// came in March does not want a tile still saying "Delivered" while their gel is in a van.
	const pill = tilePill(whitening)

	return (
		<>
			<HStack gap={12}>
				<Tile
					// A tick, for the things you get. The drop it replaced is this app's mark for
					// a cleaning, which is one item on the list rather than the list itself; the
					// file that replaced that lost its corner fold at fifty-six points and read
					// as a folder. The tick's other uses here are small marks inside controls,
					// so at tile size it competes with nothing.
					glyph="check"
					title="What's Included"
					// What is included is what the tile opens; the chevron says it can be opened.
					line={planNew > 0 ? `${planNew} New` : undefined}
					tone="blush"
					onPress={() => setMembershipOpen(true)}
				/>
				<Tile
					// The sparkle while whitening is still something on offer, the van once there
					// is a thing in transit: the subject has stopped being part of the plan and
					// become a delivery, and the mark should say which. It is the same van the
					// kit page puts in its tracking row, so the tile and the row are plainly
					// about one thing.
					glyph={tileInTransit(whitening) ? 'van' : 'sparkle'}
					title="Whitening"
					accessibilityLabel={`Whitening. ${pill?.label ?? ''}`}
					// A state, not a sentence — and one treatment for all seven of them, the same
					// grey pill with an ink mark that the rest of the app puts on a white card. A
					// coral tag stood here for the claim and mint for a delivery, which meant the
					// one tile on this screen that changes week to week was also the one that
					// changed what kind of object it was.
					lineNode={
						pill && (
							<StatusPill
								status={pill.status}
								label={pill.label}
								glyph={pill.glyph}
								neutral
							/>
						)
					}
					onPress={() => router.navigate('/whitening')}
				/>
			</HStack>
			<Sheet open={membershipOpen} onClose={() => setMembershipOpen(false)}>
				<MembershipSheet />
			</Sheet>
		</>
	)
}
